// In-app dossier shop — browse list hook.
//
// GET /api/v1/shop/drops (server/src/routes/shop.rs::list_shop_drops) is
// the same PUBLIC endpoint the Astro storefront (apps/public-site/src/
// pages/shop/index.astro) builds against — no auth, IP-rate-limited. This
// hook brings that primary-market browse into the signed-in SPA so a user
// can buy dossiers from the platform without leaving the app. Shape mirrors
// the Astro `ShopDropResponse` exactly.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export interface ShopDropResponse {
  drop_id: string;
  portfolio_id: string;
  portfolio_title: string;
  portfolio_theme: string | null;
  portfolio_description: string | null;
  /** Wax-seal base color (migration 0079); null = default bordeaux. */
  portfolio_seal_color: string | null;
  kind: "regular" | "special" | "welcome";
  status: "live" | "retiring";
  price_cents: number;
  per_user_cap: number | null;
  scheduled_at: string;
  live_at: string | null;
  retiring_at: string | null;
  creator_count: number;
  first_creator_names: string[];
  linked_bulletin_post_id: string | null;
  /** Parent portfolio is stalled (consent-withdrawal threshold). Still
   *  listed, but greyed, never featured, and not purchasable. */
  stalled: boolean;
}

interface ShopListBody {
  data: ShopDropResponse[];
  page_info: { total: number };
}

async function fetchShopDrops(): Promise<ShopDropResponse[]> {
  const response = await fetch("/api/v1/shop/drops", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`shop drops fetch failed: ${response.status}`);
  }
  const body = (await response.json()) as ShopListBody;
  return body.data;
}

export function useShopDrops(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["shop-drops"],
    queryFn: fetchShopDrops,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
    // AppShell reads this same cache to light the Shop nav's drop dot; it
    // passes `enabled: signedIn` so the query never fires for signed-out
    // chrome. The ShopPage leaves it enabled (default) — one shared,
    // 30s-stale cache entry serves both.
    enabled: options?.enabled ?? true,
  });
}
